
import { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../services/firebase";
import { useNavigate } from "react-router-dom";
import { GoogleMap, Marker, InfoWindow, Polyline } from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: "85vh",
};

const defaultCenter = {
  lat: -37.8136,
  lng: 144.9631,
};

const getMillis = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "object" && "seconds" in value) {
    return value.seconds * 1000 + (value.nanoseconds || 0) / 1_000_000;
  }
  return null;
};

const formatTime = (value) => {
  const ms = getMillis(value);
  if (!ms) return "—";
  return new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelativeMinutes = (value) => {
  const ms = getMillis(value);
  if (!ms) return null;
  const diffMin = Math.round((Date.now() - ms) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 minute ago";
  return `${diffMin} minutes ago`;
};

const isOffline = (timestamp) => {
  const ms = getMillis(timestamp);
  if (!ms) return true;
  const diff = Date.now() - ms;
  return diff > 5 * 60 * 1000;
};

const getStatus = (loc) => {
  if (isOffline(loc.timestamp)) return "Offline";
  if (loc.breakStartedAt && !loc.breakEndedAt) return "On Break";
  if (loc.breakEndedAt) return "Finished Break";
  return "Active";
};

const getMarkerIcon = (loc) => {
  const status = getStatus(loc);
  switch (status) {
    case "On Break":
      return "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
    case "Finished Break":
      return "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
    case "Offline":
      return "http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png";
    case "Active":
    default:
      return "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
  }
};

const pathOptions = {
  strokeOpacity: 0.9,
  strokeWeight: 3,
  strokeColor: "#10b981",
};

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const getAssignmentVehicleLabel = (assignment = {}) => {
  const runs = assignment.runs || {};
  const runMeta = assignment.runMeta || {};
  const labels = Object.entries(runs)
    .map(([runKey, list]) => {
      const jobs = Array.isArray(list) ? list.filter(Boolean) : [];
      const firstJob =
        jobs.find((item) => item && !item.isWeighbridgeDocket) || jobs[0] || null;
      const meta = runMeta?.[runKey] || {};
      const rego = firstNonEmpty(
        meta.vehicleRego,
        meta.rego,
        meta.truckRego,
        firstJob?.vehicleRego,
        firstJob?.rego,
        firstJob?.truckRego
      );
      const loadType = firstNonEmpty(
        meta.vehicleLoadType,
        meta.loadType,
        meta.unitType,
        meta.truckType,
        firstJob?.vehicleLoadType,
        firstJob?.loadType,
        firstJob?.unitType,
        firstJob?.truckType
      );
      return firstNonEmpty(
        meta.vehicleLabel,
        meta.truckLabel,
        firstJob?.vehicleLabel,
        firstJob?.truckLabel,
        [rego, loadType].filter(Boolean).join(" • "),
        rego
      );
    })
    .filter(Boolean);

  return Array.from(new Set(labels)).join(", ");
};

export default function AdminLiveMap() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tick, setTick] = useState(0);
  const mapRef = useRef(null);

  useEffect(() => {
    const checkAccess = async () => {
      const user = auth.currentUser;
      if (!user) return navigate("/login");

      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists() || snap.data().role !== "admin") {
        navigate("/not-authorized");
      }
    };
    checkAccess();
  }, [navigate]);

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "locations"), async (snap) => {
      const now = new Date();
      const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
      const todayKey = local.toISOString().slice(0, 10);

      const data = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const driverId = docSnap.id;

          const driverRef = doc(db, "users", driverId);
          const assignmentRef = doc(db, "assignments", `${driverId}_${todayKey}`);

          const [driverSnap, assignmentSnap] = await Promise.all([
            getDoc(driverRef),
            getDoc(assignmentRef),
          ]);

          const email = driverSnap.exists() ? driverSnap.data().email : "Unknown";
          const assignment = assignmentSnap.exists() ? assignmentSnap.data() : {};

          return {
            id: driverId,
            email,
            vehicleLabel: getAssignmentVehicleLabel(assignment),
            ...docSnap.data(),
            breakStartedAt: assignment.breakStartedAt || null,
            breakEndedAt: assignment.breakEndedAt || null,
            breakDuration: assignment.breakDuration || null,
            currentJob: assignment.currentJob || null,
          };
        }),
      );

      setLocations(data);
    });

    return () => unsub();
  }, []);

  const onMapLoad = (mapInstance) => {
    mapRef.current = mapInstance;
  };

  const handleSelectDriver = (loc) => {
    setSelectedMarker(loc);
    if (mapRef.current && loc.lat && loc.lng) {
      mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
      mapRef.current.setZoom(13);
    }
  };

  const filteredLocations = useMemo(() => {
    if (!searchTerm.trim()) return locations;
    const term = searchTerm.toLowerCase();
    return locations.filter((loc) =>
      `${loc.vehicleLabel || ""} ${loc.email || ""}`.toLowerCase().includes(term)
    );
  }, [locations, searchTerm]);

  const statusCounts = useMemo(() => {
    const counts = {
      Active: 0,
      "On Break": 0,
      "Finished Break": 0,
      Offline: 0,
    };
    locations.forEach((loc) => {
      counts[getStatus(loc)]++;
    });
    return counts;
  }, [locations]);

  const lastUpdatedMs = useMemo(() => {
    if (!locations.length) return null;
    const times = locations.map((loc) => getMillis(loc.timestamp)).filter(Boolean);
    if (!times.length) return null;
    return Math.max(...times);
  }, [locations]);

  const lastUpdatedText = useMemo(() => {
    if (!lastUpdatedMs) return "Waiting for location data...";
    const rel = formatRelativeMinutes(lastUpdatedMs);
    if (!rel) return "Waiting for location data...";
    const secondsSince = Math.floor((Date.now() - lastUpdatedMs) / 1000);
    const cycle = 10;
    const remaining = cycle - (secondsSince % cycle);
    return `Live auto-refresh · last update ${rel} · next refresh in ${remaining}s`;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick isn't read directly; it's the interval-driven trigger that recomputes the countdown text every second
  }, [lastUpdatedMs, tick]);

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              Admin
            </span>
            <h1 className="text-lg font-semibold tracking-tight text-gray-950">Live map</h1>
          </div>
          <div className="text-xs text-gray-500">{lastUpdatedText}</div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
          <span><span className="font-semibold text-red-600">{statusCounts["Active"]}</span> active</span>
          <span><span className="font-semibold text-amber-500">{statusCounts["On Break"]}</span> on break</span>
          <span><span className="font-semibold text-emerald-600">{statusCounts["Finished Break"]}</span> finished break</span>
          <span><span className="font-semibold text-sky-600">{statusCounts["Offline"]}</span> offline</span>
          <span><span className="font-semibold text-gray-900">{locations.length}</span> tracked units</span>
        </div>
      </div>

      <div className="flex flex-col gap-4 xl:flex-row">
        <div className="xl:w-80 overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-4">
            <div className="text-base font-semibold text-gray-900">Trucks</div>
            <p className="mt-0.5 text-xs text-gray-500">
              Search and select a truck to focus the map.
            </p>
            <input
              type="text"
              placeholder="Search by rego or email"
              className="mt-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="max-h-[85vh] overflow-y-auto text-sm">
            {filteredLocations.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No trucks found.</div>
            ) : (
              filteredLocations.map((loc) => {
                const status = getStatus(loc);
                const isSelected = selectedMarker?.id === loc.id;

                return (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => handleSelectDriver(loc)}
                    className={`w-full border-b border-gray-100 px-4 py-3 text-left transition ${
                      isSelected ? "bg-emerald-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {loc.vehicleLabel || loc.email}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          status === "Active"
                            ? "bg-red-100 text-red-700"
                            : status === "On Break"
                              ? "bg-amber-100 text-amber-700"
                              : status === "Finished Break"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-sky-100 text-sky-700"
                        }`}
                      >
                        {status}
                      </span>
                    </div>

                    <div className="mt-1 text-[11px] text-gray-500">
                      Last seen: {formatTime(loc.timestamp)} ({formatRelativeMinutes(loc.timestamp) || "—"})
                    </div>

                    {loc.vehicleLabel && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        Driver: {loc.email}
                      </div>
                    )}

                    <div className="mt-2 text-[11px] text-gray-600 line-clamp-2">
                      <span className="font-semibold">Current job:</span> {loc.currentJob || "—"}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Live map</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Red = active, yellow = on break, green = finished break, blue = offline.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-[11px] text-gray-600">
                <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500" />Active</span>
                <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-amber-400" />On break</span>
                <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-500" />Finished break</span>
                <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-sky-400" />Offline</span>
              </div>
            </div>
          </div>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={defaultCenter}
            zoom={11}
            onLoad={onMapLoad}
          >
            {locations.map((loc) => {
              if (!loc.path || !Array.isArray(loc.path) || loc.path.length < 2) {
                return null;
              }
              const path = loc.path
                .filter((p) => p && typeof p.lat === "number" && typeof p.lng === "number")
                .map((p) => ({ lat: p.lat, lng: p.lng }));

              if (path.length < 2) return null;

              return <Polyline key={`${loc.id}-path`} path={path} options={pathOptions} />;
            })}

            {locations.map((loc) => (
              <Marker
                key={loc.id}
                position={{ lat: loc.lat, lng: loc.lng }}
                label={(loc.vehicleLabel || loc.email || "truck").slice(0, 10)}
                icon={getMarkerIcon(loc)}
                onClick={() => handleSelectDriver(loc)}
              />
            ))}

            {selectedMarker && (
              <InfoWindow
                position={{
                  lat: selectedMarker.lat,
                  lng: selectedMarker.lng,
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="max-w-xs space-y-1 text-xs text-gray-800">
                  <p className="font-bold">{selectedMarker.vehicleLabel || selectedMarker.email}</p>
                  {selectedMarker.vehicleLabel && (
                    <p>
                      <span className="font-semibold">Driver:</span> {selectedMarker.email}
                    </p>
                  )}
                  <p>
                    <span className="font-semibold">Last seen:</span> {formatTime(selectedMarker.timestamp)} ({formatRelativeMinutes(selectedMarker.timestamp) || "—"})
                  </p>
                  <p>
                    <span className="font-semibold">Status:</span> {getStatus(selectedMarker)}
                  </p>
                  <p>
                    <span className="font-semibold">Current job:</span> {selectedMarker.currentJob || "—"}
                  </p>
                  {selectedMarker.breakStartedAt && (
                    <p>
                      <span className="font-semibold">Break started:</span> {formatTime(selectedMarker.breakStartedAt)}
                    </p>
                  )}
                  {selectedMarker.breakEndedAt && (
                    <p>
                      <span className="font-semibold">Break ended:</span> {formatTime(selectedMarker.breakEndedAt)} {selectedMarker.breakDuration ? `(${selectedMarker.breakDuration})` : ""}
                    </p>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
}
